/**
 * Error handling utilities for API responses.
 *
 * Works with backend error format:
 * { detail: { code: "ERROR_CODE", message: "...", error_id: "...", timestamp: "...",
 *             field?: "...", details?: {...} } }
 *
 * Usage:
 *   import { getErrorMessage, getFieldErrors, getErrorId } from '@/shared/utils';
 *
 *   try {
 *     await mutation.mutateAsync(data);
 *   } catch (error) {
 *     const message = getErrorMessage(error);  // Translated!
 *     const fieldErrors = getFieldErrors(error);  // For form validation
 *     const errorId = getErrorId(error);  // For user to report
 *   }
 */

import { AxiosError, isAxiosError } from 'axios';
import i18n from '../../i18n/config';
import { localizeErrorStatusParams } from './errorStatusLabels';

export interface ApiErrorDetail {
  code: string;
  message: string;
  error_id?: string;
  /** Server-stamped time the error was handled (ISO-8601 UTC, second precision). */
  timestamp?: string;
  field?: string;
  // Details can be either field validation errors or interpolation values for i18n
  details?: Record<string, { code: string; message: string } | string | number | boolean>;
}

export interface ApiError {
  response?: {
    data?: {
      detail?: ApiErrorDetail | string;
    };
    status?: number;
  };
}

/**
 * Re-parse a **blob** download's error body so the rest of this module can read it.
 *
 * A request made with ``responseType: 'blob'`` gets a Blob back on the FAILURE
 * path too — axios does not know the 4xx body is JSON — so
 * ``error.response.data.detail`` is ``undefined`` and every function below
 * falls through to the generic "an unexpected error occurred" copy. A download
 * route that refuses with a specific, actionable message (a row cap and how to
 * get under it) therefore says nothing at all to the user.
 *
 * Await this before ``getErrorMessage`` in any blob-download catch:
 *
 * ```ts
 * } catch (e) {
 *   toast.error(getErrorMessage(await readBlobError(e)));
 * }
 * ```
 *
 * Returns the error unchanged when the body is not a Blob, is not JSON, or
 * cannot be read — a failure to decode a failure must never become the thing
 * the user sees.
 */
export async function readBlobError(error: unknown): Promise<unknown> {
  const axiosError = error as AxiosError<unknown>;
  const body = axiosError?.response?.data;
  if (!(body instanceof Blob)) return error;
  try {
    const parsed = JSON.parse(await blobText(body));
    return { ...axiosError, response: { ...axiosError.response, data: parsed } };
  } catch {
    return error;
  }
}

/**
 * Read a Blob as text in both a browser and jsdom.
 *
 * `Blob.prototype.text()` is the one-liner every browser has and jsdom does
 * NOT — its Blob exposes only `slice`, and wrapping it in a `Response` yields
 * the literal string "[object Blob]" rather than the bytes. So a helper written
 * the obvious way works in production and is provably untestable, which is how
 * the blob-error gap stayed invisible in the first place. `FileReader` is
 * present in both and is the fallback.
 */
function blobText(body: Blob): Promise<string> {
  if (typeof body.text === 'function') return body.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(body);
  });
}

/**
 * Extract error detail from an API error response.
 */
export function getErrorDetail(error: unknown): ApiErrorDetail | null {
  const axiosError = error as AxiosError<{ detail?: ApiErrorDetail | string }>;
  const detail = axiosError?.response?.data?.detail;

  if (!detail) return null;

  // Handle string detail (legacy format)
  if (typeof detail === 'string') {
    return { code: 'UNKNOWN_ERROR', message: detail };
  }

  return detail;
}

/**
 * Get a translated error message from an API error.
 *
 * Uses the error code as an i18n key in the 'errors' namespace.
 * Falls back to the English message from the backend if no translation exists.
 *
 * @param error - The error from an API call
 * @param interpolation - Optional interpolation values for the translation
 * @returns Translated error message
 */
export function getErrorMessage(
  error: unknown,
  interpolation?: Record<string, string | number>
): string {
  const detail = getErrorDetail(error);
  const localTimestamp = formatTimestamp();

  if (!detail) {
    // Fallback for non-API errors - include diagnostic info
    const axiosError = error as AxiosError;

    // Connectivity failure: axios ERR_NETWORK / "Network Error" / a TypeError
    // from a failed fetch() ("Failed to fetch", incl. CORS).
    if (isNetworkError(error)) {
      return translateErrorReference('errors:NETWORK_ERROR', {
        timestamp: localTimestamp,
        defaultValue: 'Network error. Check your connection and try again.',
      });
    }

    // Timeout — axios (`ECONNABORTED`) and its fetch-side equivalent. A request
    // cut off by `fetchWithDeadline` (shared/api/fetchDeadline.ts) rejects with a
    // DOMException named `TimeoutError`, or `AbortError` on iOS 15.0–15.3, which
    // ignores `AbortController.abort(reason)`. Neither is an AxiosError and
    // neither is `instanceof Error` in every engine, so this must be matched by
    // name — otherwise it falls through to the standard SERVICE_UNAVAILABLE copy
    // and the deadline fires without the user learning why.
    if (axiosError?.code === 'ECONNABORTED' || isAbortError(error)) {
      return translateErrorReference('errors:TIMEOUT', {
        timestamp: localTimestamp,
        defaultValue: 'Request timed out. Please try again.',
      });
    }

    // A genuine non-Axios Error carrying its own message (e.g. a client-side
    // validation throw) surfaces that message. An AxiosError is deliberately
    // excluded: with no `{ detail }` envelope (a proxy/gateway 5xx with an empty
    // body, or any un-enveloped HTTP failure) its `.message` is the bare,
    // untranslated "Request failed with status code 500" — the error-handling
    // contract forbids surfacing that, so it falls through to the standard
    // SERVICE_UNAVAILABLE message + reference below.
    if (!isAxiosError(error) && error instanceof Error && error.message) {
      return error.message;
    }

    // A 403 with no envelope is CATEGORICALLY different from a 5xx with none,
    // and telling the user otherwise is worse than saying nothing. Every
    // application 403 carries `{ detail }` (`api_error(...)`, and even FastAPI's
    // own bearer rejection sends a string `detail`), so reaching here means the
    // request was refused BEFORE the backend ever saw it — in production, by the
    // WAF at the CloudFront edge (`components/waf.py`).
    //
    // The generic SERVICE_UNAVAILABLE copy is wrong three ways for that: it
    // implies an outage, it says "please try again" when the same body is
    // rejected deterministically every time, and its reference is a bare client
    // timestamp with an empty errorId because no backend request ever ran, so
    // support has nothing to look up. On 2026-08-25 a user retried three times.
    //
    // So: name the real cause, do NOT invite a retry, and give a remedy that can
    // actually work (edit the content). The timestamp is the only handle support
    // has — it is what locates the request in `aws-waf-logs-panluma-{env}` —
    // which is why this copy asks for it rather than for an errorId that does
    // not exist. The 5xx branch below is deliberately unchanged: a 500 with no
    // body IS transient, and retrying it is correct advice.
    if (isEdgeRejection(error)) {
      return translateErrorReference('errors:EDGE_REQUEST_BLOCKED', {
        timestamp: localTimestamp,
        defaultValue:
          'This content was rejected by a security filter before it reached the ' +
          'server. Retrying will not help — remove any links, code or unusual ' +
          'characters from the text, or report it using the support widget at ' +
          'the bottom corner. Reference: {{timestamp}}.',
      });
    }

    return translateErrorReference('errors:SERVICE_UNAVAILABLE', {
      timestamp: localTimestamp,
      defaultValue: 'Service temporarily unavailable. Please try again.',
    });
  }

  // An envelope with no usable code can't be translated: there is no key to look
  // up, and i18next given `defaultValue: undefined` returns the literal key. Show
  // the backend's message when there is one, and the standard unexpected-error
  // copy when there isn't — FastAPI's own request-validation 422 (`detail` is a
  // LIST of validation objects, not the PanLuma envelope) has neither.
  if (typeof detail.code !== 'string' || !detail.code) {
    return detail.message?.trim() ? detail.message : getGenericErrorMessage(error);
  }

  // Try to translate the error code
  // Use details from the API response for interpolation if available
  const translationKey = `errors:${detail.code}`;
  const translated = translateErrorReference(translationKey, {
    // The server stamps the moment it handled the error; only fall back to the
    // browser clock when the response predates that field.
    timestamp: detail.timestamp ?? localTimestamp,
    // An enveloped error usually HAS an id — but not always: a legacy string
    // `detail` resolves to UNKNOWN_ERROR carrying neither, and that code's copy
    // quotes one. `translateErrorReference` takes the id-less arm there.
    errorId: detail.error_id,
    params: {
      // Backend-provided interpolation values. Enum-valued params (`status`,
      // `po_status`) arrive as the raw column token and are swapped for the
      // viewer's localized label first — otherwise every locale renders the
      // English enum value (`errorStatusLabels.ts`).
      ...localizeErrorStatusParams(detail.code, detail.details),
      ...interpolation,                  // Caller overrides
    },
    defaultValue: detail.message,
  });

  // An untranslated code whose envelope carries no message resolves to the empty
  // string. This return value is piped straight into `toast.error(...)` by the
  // global MutationCache handler, so an empty string is not a vague message — it
  // is a toast with no text, which the user reads as the button doing nothing.
  // The contract is that an unexpected error still shows the standard copy plus
  // the reference that locates the backend log line.
  return translated.trim() ? translated : getGenericErrorMessage(error);
}

/**
 * Translate a bare `(code, message)` pair the same way `getErrorMessage`
 * translates an API error envelope.
 *
 * For error channels that are not HTTP responses — the chat turn runner
 * publishes `{type: 'error', code, message}` over the observer WebSocket — the
 * contract is identical: translate by code, and fall back to the backend's
 * English message (which is written for the log) when there is no translation.
 *
 * Never returns an empty or whitespace-only string. `useChatSession` renders the
 * return value directly as a system chat bubble, so a blank result is not a vague
 * message — it is a bubble with no text, which the user reads as the product
 * having said nothing at all. An unmapped code whose `message` is also blank
 * therefore resolves to the standard unexpected-error copy. There is no error
 * envelope here to read an `error_id` from, so that copy carries the browser
 * timestamp and an empty reference id — the same shape `getGenericErrorMessage`
 * already produces for any non-API failure.
 */
export function translateErrorCode(
  // Both halves come off an untyped WebSocket frame, so either can be absent.
  code: string | undefined,
  message: string | undefined,
  params?: Record<string, unknown>,
): string {
  if (!code) return message?.trim() ? message : getGenericErrorMessage();
  // There is no envelope here to read an `error_id` from, so this seam always
  // takes the id-less arm — and it must still SUPPLY the reference pair, because
  // i18next renders a variable it was not passed as a literal `{{errorId}}`: an
  // id-bearing code arriving over this channel used to put raw braces in a chat
  // bubble.
  const translated = translateErrorReference(`errors:${code}`, {
    defaultValue: message,
    // Same enum-token swap as `getErrorMessage` — a chat `error_notice` carries
    // the identical `details` payload, so it has the identical exposure.
    params: localizeErrorStatusParams(code, params),
  });
  return translated.trim() ? translated : getGenericErrorMessage();
}

/**
 * Format the browser's current UTC time the same way the backend stamps errors
 * (ISO-8601, second precision). Only for failures that never reached the server.
 */
function formatTimestamp(): string {
  return `${new Date().toISOString().slice(0, 19)}Z`;
}

/**
 * The i18next `context` that selects the id-LESS arm of an error key whose copy
 * ends in a support reference.
 *
 * A TRANSPORT failure produced no envelope, so there is no `error_id` to quote —
 * and the id-bearing copy then renders a hole the user is told to report:
 * "…report it using the support widget at the bottom corner. Reference:  at
 * 2026-08-24T19:23:58Z." (observed on the login page while the shared backend
 * was restarting and the vite proxy answered POST /auth/login with a plain-text
 * 500). The instruction is unactionable and the sentence reads as broken copy.
 *
 * So every key quoting `{{errorId}}` ships a `<KEY>_noId` sibling ending
 * `Reference: {{timestamp}}.` — the shape `SERVICE_UNAVAILABLE`, `NETWORK_ERROR`
 * and `TIMEOUT` already had, because those can only ever arise WITHOUT an id.
 * The timestamp stays in both arms: with no id it is the only thing that locates
 * the event. This is the same `<KEY>_<context>` idiom as
 * `RELATIONSHIP_PINNED_unnumbered`, and the whole sentence stays in the locale
 * file — never assembled from fragments in TypeScript.
 *
 * i18next falls back to the base key when the sibling is missing, which restores
 * the hole silently; `tools/check_error_reference_idless_variant.py` is what
 * keeps one from going missing.
 */
const NO_ID_CONTEXT = 'noId';

/**
 * Translate an error key that may end in a support reference, choosing the
 * id-bearing or the id-less arm on whether there is an `error_id` to quote.
 *
 * **Every seam that renders an error code goes through here** — `getErrorMessage`,
 * `translateErrorCode`, `getGenericErrorMessage`, the pre-auth `authApi` fetch
 * wrapper and the avatar provider-error frame. One path, so a key that later
 * grows a `{{errorId}}` gets the id-less arm at all of them rather than at
 * whichever one its author remembered.
 *
 * `timestamp` and `errorId` are supplied UNCONDITIONALLY (the browser clock
 * stands in for a failure that never reached the server), because i18next leaves
 * a variable it was not passed as a literal `{{errorId}}` on screen.
 *
 * @param key - Namespaced i18n key, e.g. `errors:UNEXPECTED_ERROR`.
 * @param options.params - Backend/caller interpolation values; they override the
 *   reference pair and may carry their own `context` (a producer-chosen variant
 *   such as `RELATIONSHIP_PINNED_unnumbered` wins over `_noId`).
 * @param options.defaultValueNoId - English fallback for the id-less arm; falls
 *   back to `defaultValue` when the two arms read the same.
 */
export function translateErrorReference(
  key: string,
  options: {
    errorId?: string | null;
    timestamp?: string | null;
    defaultValue?: string;
    defaultValueNoId?: string;
    params?: Record<string, unknown>;
  } = {}
): string {
  const { errorId, timestamp, defaultValue, defaultValueNoId, params } = options;
  const hasErrorId = typeof errorId === 'string' && errorId.length > 0;

  return i18n.t(key, {
    ...(hasErrorId ? {} : { context: NO_ID_CONTEXT }),
    timestamp: timestamp ?? formatTimestamp(),
    errorId: errorId ?? '',
    ...params,
    defaultValue: hasErrorId ? defaultValue : defaultValueNoId ?? defaultValue,
  });
}

/**
 * Returns a unified "generic / unexpected" error message including a reference
 * (error ID + timestamp) so the user can quote it to support. Use this anywhere
 * we previously showed `common:errors.unexpected` or a hand-rolled "something
 * went wrong" string.
 *
 * - If `error` is an API error, the server's `error_id` + `timestamp` are shown —
 *   that pair is what locates the log line.
 * - A caller holding a reference the server minted through some OTHER channel
 *   passes it as `reference`. The app-level `ErrorBoundary` is the case: a render
 *   crash never produced an API envelope, but it now POSTs itself to
 *   `/api/v1/monitoring/client-errors` and gets back the id of the durable
 *   record — so the message can quote a real reference instead of a bare clock.
 * - Otherwise (network failure, a crash whose report did not land) there is no
 *   server time to report, so the browser clock stands in, no ID is available,
 *   and `translateErrorReference` takes the id-less `_noId` arm.
 */
export function getGenericErrorMessage(
  error?: unknown,
  reference?: { errorId?: string | null; timestamp?: string | null }
): string {
  const detail = error !== undefined ? getErrorDetail(error) : null;

  return translateErrorReference('errors:UNEXPECTED_ERROR', {
    timestamp: reference?.timestamp ?? detail?.timestamp,
    // Empty on EVERY transport failure that produced no reference — which is
    // most of this function's traffic, since it is the fallback for a request
    // that never reached the server.
    errorId: reference?.errorId ?? detail?.error_id,
    defaultValue:
      'An unexpected error occurred. Please try again. If this recurs, please report it using the support widget at the bottom corner. Reference: {{errorId}} at {{timestamp}}.',
    defaultValueNoId:
      'An unexpected error occurred. Please try again. If this recurs, please report it using the support widget at the bottom corner. Reference: {{timestamp}}.',
  });
}

/**
 * Message for a surface that renders the failure IN PLACE (an inline alert on a
 * page) rather than as a toast.
 *
 * `getErrorMessage` falls back to the backend's English `detail.message` when a
 * code has no translation — acceptable for a transient toast, but an inline panel
 * that persists must not park an untranslated, log-oriented sentence on the page.
 * So: translate by code when the code is mapped, otherwise show the standard
 * system-error message with its errorId + timestamp reference
 * (`docs/platform/error-handling-design.md`).
 */
export function getCodedOrGenericErrorMessage(error: unknown): string {
  // An edge rejection has no envelope and therefore no code, so the mapped-code
  // test below can never reach it — an inline alert would park the standard
  // unexpected-error copy with a permanently blank errorId on the page, which is
  // the exact defect this file's 403 branch exists to fix, arrived at through a
  // different door. Inline is the WORSE surface for it too: a toast is gone in
  // seconds, while a persistent panel keeps telling the user their content
  // problem is an unexplained system failure.
  if (isEdgeRejection(error)) return getErrorMessage(error);

  const detail = getErrorDetail(error);
  if (detail && i18n.exists(`errors:${detail.code}`)) {
    return getErrorMessage(error);
  }
  return getGenericErrorMessage(error);
}

/**
 * Get field-level errors from a validation error response.
 * Handles both multi-field validation errors (details object) and single-field errors (field property).
 *
 * Never maps a field to an empty or whitespace-only message: an unmapped code
 * whose backend message is blank marks the field invalid with no text next to
 * it, which reads as the form rejecting the value for no stated reason. Such a
 * field gets the standard unexpected-error copy plus this envelope's reference.
 *
 * @param error - The error from an API call
 * @returns Record of field names to translated error messages, or null if not a field error
 */
export function getFieldErrors(
  error: unknown
): Record<string, string> | null {
  const detail = getErrorDetail(error);

  if (!detail) return null;

  const fieldErrors: Record<string, string> = {};

  // `details` is the form-field channel: an OBJECT entry is `{code, message}`
  // under the input's name and paints that field. A SCALAR entry beside it is
  // an interpolation param the copy quotes (`entered`, `unit`, `decimal_places`
  // on QUANTITY_PRECISION_EXCEEDED) — it names no input, so handing it to
  // `setError` would register an error on a field the form never declared and
  // wedge `handleSubmit` for the dialog's life (ui-form-errors.md § 4). Split
  // the two, and feed the scalars to every field message so the inline copy
  // interpolates exactly as the toast does.
  const fieldEntries: Array<[string, { code: string; message: string }]> = [];
  const params: Record<string, string | number | boolean> = {};
  if (detail.details) {
    for (const [key, value] of Object.entries(detail.details)) {
      if (value !== null && typeof value === 'object') {
        fieldEntries.push([key, value]);
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        params[key] = value;
      }
    }
  }

  // Handle multi-field validation errors (details object)
  if (fieldEntries.length > 0) {
    for (const [field, fieldError] of fieldEntries) {
      const translationKey = `errors:${fieldError.code}`;
      // Through the same seam as every other code, even though no field-level
      // code carries a reference tail TODAY. The alternative — a raw `i18n.t`
      // here — is what makes "every seam goes through one place" a claim rather
      // than an invariant: the day a validation code grows a
      // "Reference: {{errorId}} …" the dangling reference comes back at a seam
      // no gate watches, since the gate reads the locale file and not the call.
      const translated = translateErrorReference(translationKey, {
        errorId: detail.error_id,
        timestamp: detail.timestamp,
        params: { ...params, field },
        defaultValue: fieldError.message,
      });
      fieldErrors[field] = translated.trim() ? translated : getGenericErrorMessage(error);
    }
  }

  // Handle single-field errors (field property on main error)
  if (detail.field && !fieldErrors[detail.field]) {
    const translationKey = `errors:${detail.code}`;
    const translated = translateErrorReference(translationKey, {
      errorId: detail.error_id,
      timestamp: detail.timestamp,
      params: { ...params, field: detail.field },
      defaultValue: detail.message,
    });
    fieldErrors[detail.field] = translated.trim()
      ? translated
      : getGenericErrorMessage(error);
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
}

/**
 * Check if an error has a specific error code.
 */
export function hasErrorCode(error: unknown, code: string): boolean {
  const detail = getErrorDetail(error);
  return detail?.code === code;
}

/**
 * Get the HTTP status code from an error.
 */
export function getErrorStatus(error: unknown): number | null {
  const axiosError = error as AxiosError;
  return axiosError?.response?.status ?? null;
}

/**
 * Check if an error is a connectivity / network failure rather than an HTTP
 * error with a response. True for axios `ERR_NETWORK` (request made, no reply —
 * the offline signature), a `"Network Error"` message, or a `TypeError` from a
 * failed `fetch()` ("Failed to fetch"). These are exactly the failures a device
 * produces when it has no network — the cold-start Airplane-Mode case.
 *
 * Used to keep background read-query toasts calm: a connectivity failure means
 * "you're offline", which the persistent OfflineBanner already communicates, so
 * the per-query "Failed to load" toast is suppressed even before the async
 * offline signal (onlineManager / OfflineStatusContext) has propagated.
 */
export function isNetworkError(error: unknown): boolean {
  const axiosError = error as AxiosError;
  if (axiosError?.code === 'ERR_NETWORK' || axiosError?.message === 'Network Error') {
    return true;
  }
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  return false;
}

/**
 * A 403 carrying no `{ detail }` envelope: the request was refused BEFORE it
 * reached the application.
 *
 * The envelope is what makes this decidable. Every application 403 has one —
 * `api_error(...)` produces the object form, and FastAPI's own bearer rejection
 * produces a string `detail`, which `getErrorDetail` also resolves — so an
 * enveloped 403 (`PERMISSION_DENIED`, "Not authenticated") never lands here and
 * keeps its own translated copy. What is left is an edge rejection: in
 * production the WAF at CloudFront (`infrastructure/pulumi/components/waf.py`),
 * whose 403 body is AWS's own HTML.
 *
 * `getErrorMessage` calls this from inside its `if (!detail)` block, which has
 * already ruled the envelope out — so that half of the condition changes no
 * outcome THERE. It is spelled anyway because this is a named predicate, not an
 * inline condition: `getTransportErrorMessage` calls it with no such guard, and
 * a predicate called `isEdgeRejection` that answers `true` for a permission
 * denial is a trap for whoever reaches for it next.
 *
 * Deliberately narrow to 403. A 5xx with no envelope is a genuinely transient
 * gateway failure and keeps the retryable SERVICE_UNAVAILABLE copy; widening
 * this predicate would tell a user that a proxy hiccup was their own content's
 * fault.
 */
export function isEdgeRejection(error: unknown): boolean {
  return getErrorStatus(error) === 403 && getErrorDetail(error) === null;
}

/**
 * A 403 the APPLICATION answered with: an access decision, not a load failure.
 *
 * The complement of {@link isEdgeRejection} over the same status. An
 * `api_error(ErrorCode.PERMISSION_DENIED, ...)` — or `ADMIN_ACCESS_REQUIRED`
 * from `require_module_admin`, or any of the module-specific denial codes —
 * arrives as the OBJECT envelope `{ detail: { code, message, ... } }`. The
 * backend ran, evaluated the reader's standing and said no, so the same request
 * is refused deterministically for as long as that standing holds: a Retry
 * cannot succeed, exactly as for the 404 and edge-403 deny paths.
 *
 * Deliberately narrower than "403 with an envelope". `getErrorDetail` also
 * resolves a LEGACY STRING detail (`{ detail: "Not authenticated" }`) by
 * synthesizing an `UNKNOWN_ERROR` code, and that shape is 401-shaped rather
 * than an access answer — a session problem wearing a 403. No route here can
 * produce it (every security scheme in `shared/dependencies/auth.py` is
 * `auto_error=False`, so FastAPI never raises its own bearer rejection), and
 * the session lifecycle belongs to the axios interceptor, not to a read
 * fallback. So a string detail is NOT an access denial: it keeps the retryable
 * failure state, where a retry after a token refresh can genuinely succeed.
 *
 * Reading the raw `detail` rather than `getErrorDetail(...)` is what makes that
 * distinction decidable — the synthesized `UNKNOWN_ERROR` is indistinguishable
 * from a real envelope carrying that code once it has been through the
 * resolver. FastAPI's own request-validation `detail` is a LIST, which is
 * `typeof 'object'`, so arrays are excluded explicitly.
 */
export function isAccessDenial(error: unknown): boolean {
  if (getErrorStatus(error) !== 403) return false;
  const raw = (error as AxiosError<{ detail?: unknown }> | null)?.response?.data?.detail;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return false;
  const code = (raw as ApiErrorDetail).code;
  return typeof code === 'string' && code.length > 0;
}

/**
 * The message for a failure that never produced an error envelope — a
 * TRANSPORT failure, not a server answer.
 *
 * Names the two the client can actually identify (the deadline in
 * `shared/api/fetchDeadline.ts` → `errors:TIMEOUT`, a dead connection →
 * `errors:NETWORK_ERROR`) and gives everything else the standard
 * unexpected-error copy plus its reference.
 *
 * Use this — not bare `getErrorMessage` — in a `catch` that can see an
 * arbitrary throw. `getErrorMessage`'s last resort for a non-Axios `Error` is
 * `error.message`, which is correct for a deliberate client-side validation
 * throw and wrong for anything foreign: a proxy or WAF answering an auth call
 * with a non-JSON body raises a `SyntaxError` out of `response.json()`, and its
 * message would land verbatim in a banner on the sign-in screen. Quoting a
 * foreign exception at the user is exactly what
 * `docs/platform/error-handling-design.md` § 2a forbids on the backend; the
 * frontend has the same duty and no gate that checks it.
 */
export function getTransportErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError;
  if (
    isNetworkError(error) ||
    isAbortError(error) ||
    axiosError?.code === 'ECONNABORTED' ||
    // An edge rejection is the fourth failure the client CAN identify, and this
    // is the surface most likely to meet one: the sign-in and /setup screens
    // parse their own responses, so a WAF answering with HTML is exactly the
    // `SyntaxError` case this function exists for. Without this arm the caller
    // gets the standard unexpected-error copy plus an errorId that is always
    // blank — true, but it names nothing the user can act on.
    isEdgeRejection(error)
  ) {
    return getErrorMessage(error);
  }
  return getGenericErrorMessage(error);
}

/**
 * A `fetch` cut short by its deadline (`shared/api/fetchDeadline.ts`).
 *
 * The abort reason is a `DOMException` named `TimeoutError` — or `AbortError` on
 * iOS 15.0–15.3, which ignores `AbortController.abort(reason)` and reports the
 * default. Matched by `name` because a `DOMException` is not `instanceof Error`
 * in every engine (jsdom included), so a structural check would miss it.
 *
 * Deliberately not exported: nothing in the app routes a DELIBERATE cancel
 * through here (a caller that needs its own cancellation belongs on
 * `axiosInstance`, whose cancellations surface as axios `ERR_CANCELED`; the two
 * components that do drive an `AbortController` — `NotesGenerator` and
 * `TestBotPanel` — each swallow their own user-cancel before reaching
 * `getErrorMessage`), so within `getErrorMessage` an abort means the deadline
 * fired. That is an assumption about the codebase, not an invariant the types
 * enforce: if you add a fetch-based feature whose user-cancel can reach
 * `getErrorMessage`, it will read as "Request timed out" — handle the cancel at
 * your call site, or narrow this predicate.
 */
function isAbortError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
}

/**
 * Check if error is a 401 Unauthorized.
 */
export function isUnauthorizedError(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/**
 * Check if error is a 403 Forbidden.
 */
export function isForbiddenError(error: unknown): boolean {
  return getErrorStatus(error) === 403;
}

/**
 * Check if error is a 404 Not Found.
 */
export function isNotFoundError(error: unknown): boolean {
  return getErrorStatus(error) === 404;
}

/**
 * Check if error is a validation error (has field-level details).
 */
export function isValidationError(error: unknown): boolean {
  const detail = getErrorDetail(error);
  return detail?.code === 'VALIDATION_ERROR' && !!detail.details;
}

/**
 * Get the error ID from an API error for debugging/reporting.
 *
 * The error_id can be used to correlate frontend errors with backend logs.
 * Users can report this ID when contacting support.
 */
export function getErrorId(error: unknown): string | null {
  const detail = getErrorDetail(error);
  return detail?.error_id ?? null;
}

/**
 * Get the error code from an API error.
 */
export function getErrorCode(error: unknown): string | null {
  const detail = getErrorDetail(error);
  return detail?.code ?? null;
}
